import React from 'react';
import { X, DollarSign, Calendar, User, Phone, CreditCard, History, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';

const PaymentHistoryModal = ({ booking, onClose }) => {
  const paymentHistory = booking.paymentHistory || [];
  
  // Helper function to calculate total amount correctly
  const calculateTotalAmount = (booking) => {
    if (booking.baseAmount !== undefined) {
      const base = parseFloat(booking.baseAmount || 0);
      const discount = parseFloat(booking.discountAmount || 0);
      return Math.max(0, base - discount);
    }
    return parseFloat(booking.totalAmount || booking.price || 0);
  };

  const totalAmount = calculateTotalAmount(booking);
  const advanceAmount = parseFloat(booking.advanceAmount || 0);
  
  // Check if advance amount is already in payment history
  const advanceInHistory = paymentHistory.some(payment => {
    const paymentAmount = parseFloat(payment.amount || 0);
    // Check if any payment matches the advance amount (within 0.01 tolerance)
    return Math.abs(paymentAmount - advanceAmount) < 0.01;
  });

  // Create initial advance payment record if it exists and isn't in history
  const initialAdvancePayment = advanceAmount > 0 && !advanceInHistory ? {
    amount: advanceAmount,
    date: booking.bookingDate || booking.date || new Date().toISOString(),
    paymentMode: booking.paymentDetails?.paymentMode || 'cash',
    paidBy: booking.bookedBy || booking.contactName || 'N/A',
    mobile: booking.bookedByPhone || booking.contactPhone || 'N/A',
    timestamp: booking.bookingDate || new Date().toISOString(),
    isInitialAdvance: true // Flag to identify initial advance
  } : null;

  // Combine initial advance with payment history
  const allPayments = initialAdvancePayment 
    ? [initialAdvancePayment, ...paymentHistory]
    : paymentHistory;

  const totalPaid = allPayments.reduce((sum, payment) => sum + parseFloat(payment.amount || 0), 0);
  const balance = Math.max(0, totalAmount - totalPaid);

  // Format payment mode for display
  const formatPaymentMode = (mode) => {
    if (!mode) return 'N/A';
    const modeMap = {
      'cash': 'Cash',
      'card': 'Card (Credit/Debit)',
      'upi': 'UPI',
      'bank_transfer': 'Bank Transfer',
      'cheque': 'Cheque',
      'online': 'Online Payment',
      'other': 'Other'
    };
    return modeMap[mode] || mode.charAt(0).toUpperCase() + mode.slice(1);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-ga-bg-surface text-ga-text-primary rounded-ga-md shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-ga-primary p-6 rounded-t-ga-md sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-ga-bg-surface rounded-full p-2">
                <History className="h-6 w-6 text-ga-primary" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-ga-text-on-primary">Payment History</h2>
                <p className="text-ga-text-on-primary/80 text-sm">Booking #{booking.id}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-ga-text-on-primary hover:opacity-80 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Booking Summary */}
        <div className="p-6 border-b border-ga-border-subtle bg-ga-bg-surface-subtle">
          <h3 className="font-semibold text-ga-text-primary mb-3">Booking Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-ga-text-muted">Service:</span>
              <p className="font-medium text-ga-text-primary">{booking.serviceName || booking.groundName || 'N/A'}</p>
            </div>
            <div>
              <span className="text-ga-text-muted">Date:</span>
              <p className="font-medium text-ga-text-primary">
                {booking.date ? format(new Date(booking.date), 'dd MMM yyyy') : 'N/A'}
              </p>
            </div>
            <div>
              <span className="text-ga-text-muted">Total Amount:</span>
              <p className="font-medium text-ga-text-primary">₹{totalAmount.toFixed(2)}</p>
            </div>
            <div>
              <span className="text-ga-text-muted">Balance:</span>
              <p className={`font-medium ${balance > 0 ? 'text-ga-warning' : 'text-ga-success'}`}>
                ₹{balance.toFixed(2)}
              </p>
            </div>
          </div>
        </div>

        {/* Payment History */}
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-ga-text-primary">
              Payment Records ({allPayments.length})
            </h3>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-ga-text-muted">Total Paid:</span>
              <span className="font-bold text-ga-success">₹{totalPaid.toFixed(2)}</span>
            </div>
          </div>

          {allPayments.length === 0 ? (
            <div className="text-center py-12 bg-ga-bg-surface-subtle rounded-ga-md">
              <DollarSign className="h-12 w-12 text-ga-text-subtle mx-auto mb-4" />
              <p className="text-ga-text-muted text-lg font-medium">No Payment Records</p>
              <p className="text-ga-text-subtle text-sm mt-1">
                No payments have been recorded for this booking yet.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {allPayments.map((payment, index) => (
                <div
                  key={index}
                  className={`border rounded-ga-md p-4 hover:shadow-md transition-shadow ${
                    payment.isInitialAdvance 
                      ? 'border-ga-info/30 bg-ga-info/10' 
                      : 'border-ga-border-subtle bg-ga-bg-surface'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`rounded-full p-2 ${
                        payment.isInitialAdvance 
                          ? 'bg-ga-info/15' 
                          : 'bg-ga-primary-soft'
                      }`}>
                        <DollarSign className={`h-5 w-5 ${
                          payment.isInitialAdvance 
                            ? 'text-ga-info' 
                            : 'text-ga-primary'
                        }`} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-ga-text-primary flex items-center gap-2">
                          {payment.isInitialAdvance ? (
                            <>
                              <span>Initial Advance Payment</span>
                              <span className="text-xs bg-ga-info/15 text-ga-info px-2 py-0.5 rounded-ga-full">
                                Booking Time
                              </span>
                            </>
                          ) : (
                            `Payment #${initialAdvancePayment ? index : index + 1}`
                          )}
                        </h4>
                        <p className="text-xs text-ga-text-muted">
                          {payment.timestamp
                            ? format(new Date(payment.timestamp), 'dd MMM yyyy, hh:mm a')
                            : payment.date
                            ? format(new Date(payment.date), 'dd MMM yyyy')
                            : 'Date not available'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-ga-success">
                        ₹{parseFloat(payment.amount || 0).toFixed(2)}
                      </p>
                      {payment.paymentMode && (
                        <p className="text-xs text-ga-text-muted mt-1 capitalize">
                          {formatPaymentMode(payment.paymentMode)}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t border-ga-border-subtle">
                    <div className="flex items-start gap-2">
                      <Calendar className="h-4 w-4 text-ga-text-subtle mt-0.5" />
                      <div>
                        <p className="text-xs text-ga-text-muted">Payment Date</p>
                        <p className="text-sm font-medium text-ga-text-primary">
                          {payment.date
                            ? format(new Date(payment.date), 'dd MMM yyyy')
                            : 'N/A'}
                        </p>
                      </div>
                    </div>

                    {payment.paymentMode && (
                      <div className="flex items-start gap-2">
                        <CreditCard className="h-4 w-4 text-ga-text-subtle mt-0.5" />
                        <div>
                          <p className="text-xs text-ga-text-muted">Payment Mode</p>
                          <p className="text-sm font-medium text-ga-text-primary">
                            {formatPaymentMode(payment.paymentMode)}
                          </p>
                        </div>
                      </div>
                    )}

                    {payment.paidBy && (
                      <div className="flex items-start gap-2">
                        <User className="h-4 w-4 text-ga-text-subtle mt-0.5" />
                        <div>
                          <p className="text-xs text-ga-text-muted">Paid By</p>
                          <p className="text-sm font-medium text-ga-text-primary">{payment.paidBy}</p>
                        </div>
                      </div>
                    )}

                    {payment.mobile && (
                      <div className="flex items-start gap-2">
                        <Phone className="h-4 w-4 text-ga-text-subtle mt-0.5" />
                        <div>
                          <p className="text-xs text-ga-text-muted">Mobile Number</p>
                          <p className="text-sm font-medium text-ga-text-primary">{payment.mobile}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Summary Footer */}
          {allPayments.length > 0 && (
            <div className="mt-6 bg-ga-success/10 border border-ga-success/30 rounded-ga-md p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-ga-success" />
                  <span className="font-semibold text-ga-text-primary">Payment Summary</span>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-4 text-sm">
                    <div>
                      <span className="text-ga-text-muted">Total Amount: </span>
                      <span className="font-medium">₹{totalAmount.toFixed(2)}</span>
                    </div>
                    <div>
                      <span className="text-ga-text-muted">Total Paid: </span>
                      <span className="font-medium text-ga-success">₹{totalPaid.toFixed(2)}</span>
                    </div>
                    <div>
                      <span className="text-ga-text-muted">Balance: </span>
                      <span className={`font-bold ${balance > 0 ? 'text-ga-warning' : 'text-ga-success'}`}>
                        ₹{balance.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Close Button */}
        <div className="p-6 border-t border-ga-border-subtle bg-ga-bg-surface-subtle sticky bottom-0">
          <button
            onClick={onClose}
            className="ga-btn-primary w-full px-4 py-2 rounded-ga-md"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentHistoryModal;

